import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button } from 'react-daisyui';

import { useAuthContext } from '@/context/AuthContext.tsx';
import useUserUpdate from '@/api/users/useUserUpdate.ts';
import Routes from '@/router/Routes.ts';
import OnboardingStep from '$/_const/OnboardingStep.ts';
import { usePostHog } from '@posthog/react';

const OnboardingWelcomePage: FC = () => {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const { mutateAsync: updateUser, isPending, error } = useUserUpdate();

  const preferredName = user?.preferred_name ?? '';

  const handleStart = () => {
    void (async () => {
      try {
        if (!user) return;

        await updateUser([
          {
            onboarding_step: OnboardingStep.DNA_YOUR_PACE,
          },
          {
            id: user.id,
          },
        ]);

        posthog?.capture('onboarding_step_completed', {
          onboarding_step: 'welcome',
        });
        navigate(Routes.ONBOARDING__DNA);
      } catch (error) {
        console.error(error);
        posthog?.capture('onboarding_step_failed', {
          onboarding_step: 'welcome',
        });
        posthog?.captureException(error, {
          operation: 'start_travel_dna',
          onboarding_step: 'welcome',
        });
      }
    })();
  };

  const handleGoBack = () => {
    void (async () => {
      try {
        if (!user || isPending) return;

        await updateUser([
          {
            onboarding_step: OnboardingStep.PREFERRED_NAME,
          },
          {
            id: user.id,
          },
        ]);

        navigate(Routes.PREFERRED_NAME);
      } catch (error) {
        console.error(error);
        posthog?.capture('onboarding_step_failed', {
          onboarding_step: 'welcome',
        });
        posthog?.captureException(error, {
          operation: 'return_to_preferred_name',
          onboarding_step: 'welcome',
        });
      }
    })();
  };

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[var(--cream)] text-[var(--ink)]">
      <div className="flex min-h-full w-full items-center justify-center px-6 py-12 sm:py-16">
        <main className="flex w-full max-w-[900px] flex-col items-center text-center">
          {error && (
            <Alert className="mb-8 w-full max-w-[600px] font-sans text-sm" status="error">
              {error.message}
            </Alert>
          )}

          <h1 className="max-w-[760px] font-feature text-[clamp(32px,4vw,42px)] font-semibold leading-[1.2] tracking-[-0.02em]">
            <Trans
              i18nKey="onboardingWelcomePage.title"
              values={{ name: preferredName }}
              components={{
                highlight: <span className="italic text-[var(--gold)]" />,
              }}
            />
          </h1>

          <div className="mt-8 max-w-[820px] font-sans text-base leading-[1.6] text-[var(--text-muted)] sm:text-lg">
            <p>{t('onboardingWelcomePage.description')}</p>

            <p className="mt-2">{t('onboardingWelcomePage.descriptionSecond')}</p>
          </div>

          <p className="mt-12 max-w-[830px] font-feature text-lg font-light italic leading-[1.45] text-[var(--gold-deep)] sm:text-xl">
            {t('onboardingWelcomePage.reassurance')}
          </p>

          <div className="mt-10 rounded-full border border-[var(--gold-deep)] bg-transparent px-6 py-3 font-sans text-sm font-semibold text-[var(--gold-deep)]">
            {t('onboardingWelcomePage.duration')}
          </div>

          <Button
            type="button"
            loading={isPending}
            onClick={handleStart}
            className="journi-primary-button mt-12 !min-h-0 w-full max-w-[312px] !rounded-[9px] !border-0 !bg-[var(--gold)] px-[34px] py-4 font-sans !text-base font-medium normal-case !text-[var(--ink)] shadow-[0_8px_24px_rgba(180,146,58,0.16)] transition-[transform,background-color,box-shadow] duration-500 ease-[cubic-bezier(.22,1,.36,1)] hover:!-translate-y-0.5 hover:!bg-[var(--gold-lt)] hover:shadow-[0_12px_28px_rgba(180,146,58,0.24)] active:!translate-y-0 active:!bg-[var(--gold-deep)]"
          >
            {t('onboardingWelcomePage.start')}
            <span aria-hidden="true">→</span>
          </Button>

          <button
            type="button"
            disabled={isPending}
            onClick={handleGoBack}
            className="mt-7 rounded-md px-4 py-2 font-sans text-xl font-medium text-[var(--ink)] transition-colors duration-300 hover:text-[var(--gold-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--gold)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('button.goBack')}
          </button>
        </main>
      </div>
    </div>
  );
};

export default OnboardingWelcomePage;
